import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api, displayName, downloadCsv, fmtDay, fmtInt, fmtPct, humanize, useQuery, useUsers } from './lib';
import {
  BarList, Btn, EmptyState, ErrorState, Page, Pagination, Panel, RefreshButton, RowsSkeleton, SearchInput,
  Segmented, StatCard, StatGrid, StatSkeletons, Table, TD, TH, TR, usePagination,
} from './ui';
import { ChartTooltip, COLORS, cursorFill, gridProps, xAxisProps, yAxisProps } from './charts';
import {
  fetchGestures, fmtGestureDay, gestureMeta, matchesOutcome, OUTCOME, OutcomePill, outcomeOptions,
} from './gestures';

const PERIODS = [
  { value: 'week', label: '7 days' },
  { value: 'month', label: '30 days' },
  { value: 'all', label: 'All time' },
];

const BUCKET_COLOR = { '0-25%': COLORS.bad, '26-50%': COLORS.warn, '51-75%': COLORS.ink2, '76-100%': COLORS.ink };
const PERIOD_DAYS = { week: 7, month: 30, all: undefined };

/* Every gesture given in the period, newest first, with what the person did. */
function AllGestures({ period, list, userById, onOpenCustomer }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const { data, error, refetch } = list;
  const counts = { ...(data?.counts || {}), total: data?.total || 0 };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.gestures || []).filter((g) => {
      if (!matchesOutcome(g, filter)) return false;
      if (!q) return true;
      const u = userById[g.user_id];
      return [g.text, g.notes, humanize(g.category), u?.email, u && displayName(u)]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [data, filter, query, userById]);
  const pg = usePagination(rows, 50, `${period}|${filter}|${query}`);

  const exportCsv = () => downloadCsv(
    `gestures-${period}.csv`,
    ['Day', 'Customer', 'Email', 'Gesture', 'Result', 'Category', 'Minutes', 'Done at', 'Note', 'Wrote own instead of'],
    rows.map((g) => {
      const u = userById[g.user_id];
      return [
        g.date, u ? displayName(u) : g.user_id, u?.email, g.text, OUTCOME[g.outcome]?.label || g.outcome,
        humanize(g.category), g.estimated_time, g.completed_at, g.notes, g.original_text,
      ];
    }),
  );

  return (
    <Panel
      flush
      title="Every gesture"
      description="Each gesture given in the period and what the person did with it. Click a row to see all of that person's gestures."
      actions={(
        <>
          <SearchInput value={query} onChange={setQuery} placeholder="Search gesture or customer" className="sm:w-60" />
          <Btn variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length}>
            <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Export CSV</span>
          </Btn>
        </>
      )}
    >
      <div className="border-b border-border px-4 py-2.5">
        <Segmented value={filter} onChange={setFilter} options={outcomeOptions(counts)} />
      </div>
      {error ? (
        <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
      ) : !data ? (
        <RowsSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState title="No gestures match" description={query ? 'Try another search.' : 'No gesture in this period has this result.'} />
      ) : (
        <>
          <Table>
            <thead>
              <tr><TH>Day</TH><TH>Customer</TH><TH>Gesture</TH><TH align="right">Result</TH></tr>
            </thead>
            <tbody>
              {pg.slice.map((g) => {
                const u = userById[g.user_id];
                return (
                  <TR key={g.suggestion_id} onClick={() => onOpenCustomer(g.user_id, 'gestures')}>
                    <TD className="whitespace-nowrap text-xs text-muted-foreground">{fmtGestureDay(g.date)}</TD>
                    <TD className="max-w-[200px]">
                      <p className="truncate text-sm text-foreground">{u ? displayName(u) : `${g.user_id.slice(0, 8)}…`}</p>
                      {u?.email && <p className="truncate text-xs text-muted-foreground">{u.email}</p>}
                    </TD>
                    <TD className="min-w-[260px]">
                      <p className="line-clamp-2 text-sm text-foreground">{g.text || 'Untitled gesture'}</p>
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{gestureMeta(g).join(' · ')}</p>
                    </TD>
                    <TD align="right"><OutcomePill outcome={g.outcome} /></TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
          <Pagination {...pg} noun="gestures" />
        </>
      )}
    </Panel>
  );
}

export default function GesturesPanel({ onOpenCustomer }) {
  const [period, setPeriod] = useState('month');
  const { data: m, error, loading, refetch, updatedAt } = useQuery(
    `gestures:${period}`,
    () => api.get(`/admin/metrics/aggregate?period=${period}`),
    { maxAge: 5 * 60_000 },
  );
  const list = useQuery(
    `gesture-list:${period}`,
    () => fetchGestures({ days: PERIOD_DAYS[period] }),
    { maxAge: 5 * 60_000 },
  );
  const { data: users } = useUsers();
  const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);

  const categories = Object.entries(m?.category_breakdown || {})
    .map(([label, value]) => ({ label: humanize(label), value }))
    .sort((a, b) => b.value - a.value);
  const buckets = Object.entries(m?.completion_distribution || {}).map(([label, value]) => ({
    label: `${label} done`, value, color: BUCKET_COLOR[label],
  }));
  const weekly = (m?.weekly_breakdown || []).map((w) => ({ ...w, label: `${fmtDay(w.week_start)}–${fmtDay(w.week_end)}` }));
  const delta = m?.prev_completion_rate != null ? m.overall_completion_rate - m.prev_completion_rate : null;

  return (
    <Page
      title="Gestures"
      description="How people use their daily gestures: how many they get, finish and skip, and what they choose."
      actions={(
        <>
          <Segmented value={period} onChange={setPeriod} options={PERIODS} />
          <RefreshButton onClick={() => { refetch(); list.refetch(); }} loading={loading || list.loading} updatedAt={updatedAt} />
        </>
      )}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}
      {!m && loading ? (
        <StatSkeletons count={8} />
      ) : m && (
        <div className={cn('space-y-4', loading && 'opacity-70 transition-opacity')}>
          <StatGrid>
            <StatCard
              label="Completion rate"
              value={fmtPct(m.overall_completion_rate, 0)}
              hint={delta != null ? `${delta >= 0 ? '+' : ''}${delta} points vs the previous period` : `Average per person ${fmtPct(m.avg_completion_rate, 0)}`}
              tone={delta != null && delta < 0 ? 'warning' : undefined}
            />
            <StatCard label="Gestures done" value={fmtInt(m.total_completed)} hint={`of ${fmtInt(m.total_assigned)} given · ${fmtInt(m.total_skipped)} skipped`} />
            <StatCard label="People with gestures" value={fmtInt(m.active_users)} hint={`${fmtInt(m.new_users)} new accounts in the period`} />
            <StatCard
              label="At risk"
              value={fmtInt(m.at_risk_users)}
              hint="Skipped 3 or more in a row"
              tone={m.at_risk_users > 0 ? 'warning' : undefined}
            />
            <StatCard label="Check-ins" value={fmtInt(m.total_checkins)} hint={`${fmtPct(m.checkin_rate, 0)} of active people checked in`} />
            <StatCard label="Relationship rating" value={m.avg_mood != null ? Number(m.avg_mood).toFixed(1) : '—'} hint="Average from check-ins" />
            <StatCard label="Time invested" value={`${fmtInt(m.total_minutes)} min`} hint="Estimated, from done gestures" />
            <StatCard label="Median completion" value={fmtPct(m.median_completion_rate, 0)} hint="Per person" />
          </StatGrid>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Completion by week" description="Share of given gestures that were done, last 4 weeks">
              {weekly.some((w) => w.total > 0) ? (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weekly} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                      <CartesianGrid {...gridProps} />
                      <XAxis dataKey="label" {...xAxisProps} minTickGap={4} />
                      <YAxis {...yAxisProps} domain={[0, 100]} unit="%" />
                      <Tooltip
                        cursor={cursorFill}
                        content={<ChartTooltip labelFormatter={(l) => l} valueFormatter={(v, p) => `${v}% · ${p.payload.completed} of ${p.payload.total}`} />}
                      />
                      <Bar dataKey="rate" name="Done" fill={COLORS.ink} radius={[3, 3, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyState title="No gestures in the last 4 weeks" />}
            </Panel>
            <Panel title="People by completion rate" description="How many people finish what share of their gestures">
              <BarList items={buckets} empty="No gestures in this period" />
            </Panel>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Done by category">
              <BarList items={categories} empty="No gestures done in this period" />
            </Panel>
            <Panel flush title="Most consistent people" description="Most gestures done in the period">
              {(m.top_users || []).length === 0 ? (
                <EmptyState title="Nobody yet" />
              ) : (
                <Table>
                  <thead><tr><TH>Customer</TH><TH align="right">Done</TH><TH align="right">Rate</TH></tr></thead>
                  <tbody>
                    {m.top_users.map((t) => {
                      const u = userById[t.user_id];
                      return (
                        <TR key={t.user_id} onClick={u ? () => onOpenCustomer(t.user_id, 'gestures') : undefined}>
                          <TD>
                            <p className="truncate text-sm text-foreground">{u ? displayName(u) : `${t.user_id.slice(0, 8)}…`}</p>
                            {u?.email && <p className="truncate text-xs text-muted-foreground">{u.email}</p>}
                          </TD>
                          <TD align="right">{fmtInt(t.completed)} / {fmtInt(t.total)}</TD>
                          <TD align="right">{fmtPct(t.rate, 0)}</TD>
                        </TR>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Panel>
          </div>
        </div>
      )}
      <AllGestures period={period} list={list} userById={userById} onOpenCustomer={onOpenCustomer} />
    </Page>
  );
}
